import React, { useState } from 'react';
import { authService } from '@/src/services/authService';
import { recordUserAcceptance } from '@/src/services/legalService';
import { TermsOfServiceScreen, PrivacyPolicyScreen } from './LegalScreens';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToSignIn: () => void;
  onSuccess: (type: 'individual' | 'company', user: any) => void;
  termsVersion?: string;
  privacyVersion?: string;
}

const SignUpModal: React.FC<Props> = ({ 
  isOpen, 
  onClose, 
  onSwitchToSignIn, 
  onSuccess,
  termsVersion = '1.0.0',
  privacyVersion = '1.0.0'
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [representativeName, setRepresentativeName] = useState('');
  const [accountType, setAccountType] = useState<'individual' | 'company'>('individual');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordRegex.test(password)) {
      setError('Password requires min 8 characters, 1 uppercase, 1 lowercase, 1 number, and 1 special character.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (!termsAccepted || !privacyAccepted) {
      setError('Please read and accept both the Terms of Service and Privacy Policy to create an account.');
      return;
    }

    setLoading(true);
    
    try {
      const { user } = await authService.signUp({
        email,
        password,
        name,
        type: accountType,
        agentName: accountType === 'company' ? representativeName : undefined
      });

      // Record legal acceptances in Firestore
      await Promise.all([
        recordUserAcceptance(user.uid, 'terms', termsVersion),
        recordUserAcceptance(user.uid, 'privacy', privacyVersion)
      ]);
      
      setLoading(false);
      onSuccess(user.type, user);
      onClose();
    } catch (err: any) {
      setLoading(false);
      setError(err.message || 'Account creation failed.');
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/75 animate-in fade-in duration-200">
      <div 
        className="w-full max-w-md bg-[#3a3430] border border-white/[0.06] rounded-xl overflow-hidden animate-in zoom-in-95 duration-200 max-h-[calc(100vh-2rem)] md:max-h-[calc(100vh-4rem)] flex flex-col text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 py-5 border-b border-white/[0.06] flex items-center justify-between bg-[#322D29] shrink-0">
          <div className="flex flex-col">
            <span className="text-[10px] font-bold uppercase text-[#F26522] tracking-[0.25em] font-heading">Get Started</span>
            <h2 className="text-base font-bold tracking-tight font-heading text-white leading-tight mt-0.5">Create an Account</h2>
          </div>
          <button 
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-white/40 hover:text-white transition-colors cursor-pointer rounded-lg hover:bg-white/5"
          >
            <i className="fas fa-times text-base"></i>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto custom-scrollbar">
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="space-y-3">
              {/* Account Type Selection */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 font-heading">Account Type</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setAccountType('individual')}
                    className={`py-2.5 text-[10px] font-bold uppercase tracking-wider rounded-lg border transition-colors cursor-pointer ${
                      accountType === 'individual' 
                        ? 'bg-[#F26522] text-white border-[#F26522]' 
                        : 'bg-[#322D29] text-white/60 border-white/[0.08] hover:border-white/20'
                    }`}
                  >
                    <i className="fas fa-user mr-2 text-xs"></i> Individual
                  </button>
                  <button
                    type="button"
                    onClick={() => setAccountType('company')}
                    className={`py-2.5 text-[10px] font-bold uppercase tracking-wider rounded-lg border transition-colors cursor-pointer ${
                      accountType === 'company' 
                        ? 'bg-[#F26522] text-white border-[#F26522]' 
                        : 'bg-[#322D29] text-white/60 border-white/[0.08] hover:border-white/20'
                    }`}
                  >
                    <i className="fas fa-building mr-2 text-xs"></i> Company
                  </button>
                </div>
              </div>

              {accountType === 'company' && (
                <div className="space-y-1.5 animate-in slide-in-from-top-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 font-heading">Representative Name</label>
                  <div className="relative">
                    <i className="fas fa-user-tie absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-xs"></i>
                    <input 
                      type="text"
                      required
                      placeholder="REPRESENTATIVE NAME"
                      className="w-full pl-10 pr-4 py-3 bg-[#322D29] border border-white/[0.08] text-white placeholder-white/30 text-sm font-medium rounded-lg outline-none focus:border-[#F26522] transition-colors font-dm"
                      value={representativeName}
                      onChange={(e) => setRepresentativeName(e.target.value)}
                    />
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 font-heading">
                  {accountType === 'individual' ? 'Full Name' : 'Company Name'}
                </label>
                <div className="relative">
                  <i className={`fas ${accountType === 'individual' ? 'fa-user' : 'fa-building'} absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-xs`}></i>
                  <input 
                    type="text"
                    required
                    placeholder={accountType === 'individual' ? 'Full Name' : 'Company Name'}
                    className="w-full pl-10 pr-4 py-3 bg-[#322D29] border border-white/[0.08] text-white placeholder-white/30 text-sm font-medium rounded-lg outline-none focus:border-[#F26522] transition-colors font-dm"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 font-heading">
                  {accountType === 'individual' ? 'Email Address' : 'Company Email Address'}
                </label>
                <div className="relative">
                  <i className="fas fa-envelope absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-xs"></i>
                  <input 
                    type="email"
                    required
                    placeholder={accountType === 'individual' ? 'user@example.com' : 'contact@company.com'}
                    className="w-full pl-10 pr-4 py-3 bg-[#322D29] border border-white/[0.08] text-white placeholder-white/30 text-sm font-medium rounded-lg outline-none focus:border-[#F26522] transition-colors font-dm"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 font-heading">Password</label>
                  <div className="relative">
                    <i className="fas fa-lock absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-xs"></i>
                    <input 
                      type="password"
                      required
                      autoComplete="new-password"
                      placeholder="••••••••"
                      className="w-full pl-10 pr-4 py-3 bg-[#322D29] border border-white/[0.08] text-white placeholder-white/30 text-sm font-medium rounded-lg outline-none focus:border-[#F26522] transition-colors font-dm"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 font-heading">Confirm Password</label>
                  <div className="relative">
                    <i className="fas fa-shield-check absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-xs"></i>
                    <input 
                      type="password"
                      required
                      autoComplete="new-password"
                      placeholder="••••••••"
                      className="w-full pl-10 pr-4 py-3 bg-[#322D29] border border-white/[0.08] text-white placeholder-white/30 text-sm font-medium rounded-lg outline-none focus:border-[#F26522] transition-colors font-dm"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {error && (
                <div className="p-3 bg-[#322D29] border border-red-500/40 rounded-lg flex items-center gap-2.5 animate-in slide-in-from-top-1">
                  <i className="fas fa-triangle-exclamation text-red-400 text-xs"></i>
                  <span className="text-[10px] font-medium text-red-200 tracking-wide">{error}</span>
                </div>
              )}
            </div>

            {/* Legal Acceptances checkboxes */}
            <div className="space-y-2.5 border-t border-b border-white/[0.06] py-4 my-2">
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  required
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="w-4 h-4 mt-0.5 rounded border-white/20 bg-[#322D29] text-[#F26522] focus:ring-[#F26522] accent-[#F26522] cursor-pointer"
                />
                <span className="text-[10px] font-medium text-white/70 uppercase tracking-wide leading-relaxed">
                  I accept the eNvesta AI{' '}
                  <span 
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowTermsModal(true); }}
                    className="text-[#F26522] hover:underline cursor-pointer inline-block"
                  >
                    Terms of Service (v{termsVersion})
                  </span>
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  required
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  className="w-4 h-4 mt-0.5 rounded border-white/20 bg-[#322D29] text-[#F26522] focus:ring-[#F26522] accent-[#F26522] cursor-pointer"
                />
                <span className="text-[10px] font-medium text-white/70 uppercase tracking-wide leading-relaxed">
                  I accept the eNvesta AI{' '}
                  <span 
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowPrivacyModal(true); }}
                    className="text-[#F26522] hover:underline cursor-pointer inline-block"
                  >
                    Privacy Policy (v{privacyVersion})
                  </span>
                </span>
              </label>
            </div>

            <div className="pt-2">
              <button 
                type="submit"
                disabled={loading || !termsAccepted || !privacyAccepted}
                className="w-full bg-[#F26522] hover:bg-[#F26522]/90 text-white py-3.5 font-bold uppercase text-[11px] tracking-[0.2em] rounded-lg transition-colors font-heading active:scale-[0.99] disabled:opacity-50 flex items-center justify-center gap-2.5 cursor-pointer"
              >
                {loading ? (
                  <>
                    <i className="fas fa-circle-notch fa-spin text-xs"></i>
                    Creating Account...
                  </>
                ) : (
                  <>
                    <i className="fas fa-user-plus text-xs"></i>
                    Create an Account
                  </>
                )}
              </button>
            </div>

            <div className="relative pt-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/[0.06]"></div>
              </div>
              <div className="relative flex justify-center text-[9px] uppercase font-bold tracking-[0.2em]">
                <span className="bg-[#3a3430] px-3 text-white/40">Already have an account?</span>
              </div>
            </div>

            <button 
              type="button"
              onClick={onSwitchToSignIn}
              className="w-full py-3 border border-white/[0.1] text-[10px] font-bold uppercase tracking-[0.15em] text-white/80 hover:text-white hover:bg-white/5 rounded-lg transition-colors cursor-pointer"
            >
              Connect an Existing Account
            </button>
          </form>
        </div>

        {/* Modal Overlay for Terms Screen */}
        {showTermsModal && (
          <div className="fixed inset-0 z-[250] bg-black/80 flex items-center justify-center p-4">
            <div className="w-full max-w-2xl bg-[#3a3430] border border-white/[0.06] rounded-xl overflow-hidden flex flex-col max-h-[85vh] text-white">
              <div className="flex justify-end p-3 bg-[#322D29] border-b border-white/[0.06]">
                <button 
                  type="button"
                  onClick={() => setShowTermsModal(false)}
                  className="px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-wider bg-white/10 text-white hover:bg-[#F26522] rounded transition-colors cursor-pointer"
                >
                  Close Document
                </button>
              </div>
              <div className="flex-1 overflow-y-auto custom-scrollbar">
                <TermsOfServiceScreen 
                  showAcceptButton={true} 
                  onAccepted={() => { setTermsAccepted(true); setShowTermsModal(false); }} 
                />
              </div>
            </div>
          </div>
        )}

        {/* Modal Overlay for Privacy Screen */}
        {showPrivacyModal && (
          <div className="fixed inset-0 z-[250] bg-black/80 flex items-center justify-center p-4">
            <div className="w-full max-w-2xl bg-[#3a3430] border border-white/[0.06] rounded-xl overflow-hidden flex flex-col max-h-[85vh] text-white">
              <div className="flex justify-end p-3 bg-[#322D29] border-b border-white/[0.06]">
                <button 
                  type="button"
                  onClick={() => setShowPrivacyModal(false)}
                  className="px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-wider bg-white/10 text-white hover:bg-[#F26522] rounded transition-colors cursor-pointer"
                >
                  Close Document
                </button>
              </div>
              <div className="flex-1 overflow-y-auto custom-scrollbar">
                <PrivacyPolicyScreen 
                  showAcceptButton={true} 
                  onAccepted={() => { setPrivacyAccepted(true); setShowPrivacyModal(false); }} 
                />
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default SignUpModal;
